<script setup lang="ts">
import { computed, ref } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import axiosInstance from '@/services/interceptor.services';
import { TranslationService } from '@/services/translation.services';
import { useCampaignToast } from './useCampaignToast';
import type { IApiResponseCategory, IDataItem } from '@/types/category.types';

const { notifySuccess, notifyError } = useCampaignToast();

interface INewEventCategoryPayload {
  designation: { fr: string; en: string; nl: string };
  description: { fr: string; en: string; nl: string };
  restaurantId: string;
  tags: string[];
}

const props = defineProps<{
  storeId: string;
  modelValue: string[];
}>();

const emit = defineEmits<{
  'update:modelValue': [categoryIds: string[]];
}>();

const search = ref('');

const {
  data: categoriesResp,
  isPending,
  refetch,
} = useQuery({
  queryKey: computed(() => ['campaign-target-categories', props.storeId]),
  queryFn: () =>
    axiosInstance.get<IApiResponseCategory>(
      `products/categories/search?pagination=false&storeIds=${props.storeId}&tags=event_menu&sortBy=name&sortOrder=ASC`,
    ),
  enabled: computed(() => !!props.storeId),
});

// Catégories créées dans cette session, ajoutées localement le temps que la
// liste serveur se rafraîchisse (évite que l'admin ne les retrouve pas de suite).
const locallyCreated = ref<IDataItem[]>([]);

const categories = computed<IDataItem[]>(() => {
  const fromServer = categoriesResp.value?.data?.data ?? [];
  const knownIds = new Set(fromServer.map((c) => c.id));
  const extra = locallyCreated.value.filter((c) => !knownIds.has(c.id));
  return [...fromServer, ...extra];
});

const filteredCategories = computed(() => {
  const q = search.value.trim().toLowerCase();
  if (!q) return categories.value;
  return categories.value.filter((c) =>
    c.designation.toLowerCase().includes(q),
  );
});

function isSelected(id: string): boolean {
  return props.modelValue.includes(id);
}
function toggle(id: string) {
  const next = isSelected(id)
    ? props.modelValue.filter((v) => v !== id)
    : [...props.modelValue, id];
  emit('update:modelValue', next);
}

// ── Création inline d'une catégorie taguée "event_menu" ─────────────────────
const createOpen = ref(false);
const nameFr = ref('');
const nameEn = ref('');
const nameNl = ref('');
const descFr = ref('');
const descEn = ref('');
const descNl = ref('');
const creating = ref(false);
const createError = ref('');

function resetCreateForm() {
  nameFr.value = '';
  nameEn.value = '';
  nameNl.value = '';
  descFr.value = '';
  descEn.value = '';
  descNl.value = '';
  createError.value = '';
}

function closeCreateForm() {
  createOpen.value = false;
  resetCreateForm();
}

const translatingNameSource = ref<'fr' | 'en' | 'nl' | null>(null);
const translatingDescSource = ref<'fr' | 'en' | 'nl' | null>(null);

const nameRefs = { fr: nameFr, en: nameEn, nl: nameNl };
const descRefs = { fr: descFr, en: descEn, nl: descNl };

async function onNameBlur(lang: 'fr' | 'en' | 'nl') {
  const value = nameRefs[lang].value;
  if (!value.trim() || translatingNameSource.value) return;
  translatingNameSource.value = lang;
  try {
    const translated = await TranslationService.translateText(value, lang);
    (['fr', 'en', 'nl'] as const)
      .filter((l) => l !== lang)
      .forEach((l) => {
        nameRefs[l].value = translated[l];
      });
  } finally {
    translatingNameSource.value = null;
  }
}

async function onDescBlur(lang: 'fr' | 'en' | 'nl') {
  const value = descRefs[lang].value;
  if (!value.trim() || translatingDescSource.value) return;
  translatingDescSource.value = lang;
  try {
    const translated = await TranslationService.translateText(value, lang);
    (['fr', 'en', 'nl'] as const)
      .filter((l) => l !== lang)
      .forEach((l) => {
        descRefs[l].value = translated[l];
      });
  } finally {
    translatingDescSource.value = null;
  }
}

async function createCategory() {
  if (!nameFr.value.trim()) {
    createError.value = 'Le nom (FR) est requis.';
    return;
  }
  creating.value = true;
  createError.value = '';
  try {
    const payload: INewEventCategoryPayload = {
      designation: {
        fr: nameFr.value.trim(),
        en: nameEn.value.trim() || nameFr.value.trim(),
        nl: nameNl.value.trim() || nameFr.value.trim(),
      },
      description: {
        fr: descFr.value.trim(),
        en: descEn.value.trim(),
        nl: descNl.value.trim(),
      },
      restaurantId: props.storeId,
      tags: ['event_menu'],
    };
    const res = await axiosInstance.post('products/categories/create', payload);
    const created: IDataItem = (res.data as any)?.data ?? (res.data as any);
    if (created?.id) {
      locallyCreated.value.push(created);
      emit('update:modelValue', [...props.modelValue, created.id]);
    }
    resetCreateForm();
    createOpen.value = false;
    await refetch();
    notifySuccess('Catégorie créée.');
  } catch (err: any) {
    const raw = err?.response?.data?.message;
    const fallback = 'Erreur lors de la création de la catégorie.';
    createError.value = Array.isArray(raw)
      ? raw.join(' · ')
      : (raw ?? fallback);
    notifyError(err, fallback);
  } finally {
    creating.value = false;
  }
}
</script>

<template>
  <div class="ctc">
    <div class="ctc__toolbar">
      <input
        v-model="search"
        class="ctc__search"
        type="text"
        placeholder="Rechercher une catégorie…"
      />
      <span class="ctc__count">{{ modelValue.length }} sélectionnée(s)</span>
    </div>

    <div v-if="isPending" class="ctc__loading">Chargement…</div>
    <div v-else-if="!filteredCategories.length" class="ctc__empty">
      Aucune catégorie trouvée.
    </div>
    <ul v-else class="ctc__list">
      <li v-for="c in filteredCategories" :key="c.id" class="ctc__item">
        <label class="ctc__label">
          <span class="ctc__checkbox">
            <input
              type="checkbox"
              :checked="isSelected(c.id)"
              @change="toggle(c.id)"
            />
            <span class="ctc__checkbox-box">
              <i class="pi pi-check" />
            </span>
          </span>
          <span>{{ c.designation }}</span>
        </label>
      </li>
    </ul>

    <button
      type="button"
      class="ctc__toggle-create"
      @click="createOpen = !createOpen"
    >
      <i class="pi" :class="createOpen ? 'pi-minus' : 'pi-plus'" />
      Nouvelle catégorie
    </button>

    <div
      class="ctc__create-collapse"
      :class="{ 'ctc__create-collapse--open': createOpen }"
    >
      <div class="ctc__create-collapse-inner">
        <div class="ctc__create-form">
          <div class="ctc__create-hd">
            <span class="ctc__create-title">Nouvelle catégorie</span>
            <button
              type="button"
              class="ctc__close-btn"
              title="Fermer"
              @click="closeCreateForm"
            >
              <i class="pi pi-times" />
            </button>
          </div>
          <div class="ctc__field-row">
            <div class="ctc__field">
              <label class="ctc__field-label">
                Nom (FR)
                <span
                  v-if="translatingNameSource && translatingNameSource !== 'fr'"
                  class="ctc__translating"
                  >Traduction…</span
                >
              </label>
              <input
                v-model="nameFr"
                class="ctc__input"
                type="text"
                @blur="onNameBlur('fr')"
              />
            </div>
            <div class="ctc__field">
              <label class="ctc__field-label">
                Nom (EN)
                <span
                  v-if="translatingNameSource && translatingNameSource !== 'en'"
                  class="ctc__translating"
                  >Traduction…</span
                >
              </label>
              <input
                v-model="nameEn"
                class="ctc__input"
                type="text"
                @blur="onNameBlur('en')"
              />
            </div>
            <div class="ctc__field">
              <label class="ctc__field-label">
                Nom (NL)
                <span
                  v-if="translatingNameSource && translatingNameSource !== 'nl'"
                  class="ctc__translating"
                  >Traduction…</span
                >
              </label>
              <input
                v-model="nameNl"
                class="ctc__input"
                type="text"
                @blur="onNameBlur('nl')"
              />
            </div>
          </div>
          <div class="ctc__field-row">
            <div class="ctc__field">
              <label class="ctc__field-label">
                Description (FR)
                <span
                  v-if="translatingDescSource && translatingDescSource !== 'fr'"
                  class="ctc__translating"
                  >Traduction…</span
                >
              </label>
              <input
                v-model="descFr"
                class="ctc__input"
                type="text"
                @blur="onDescBlur('fr')"
              />
            </div>
            <div class="ctc__field">
              <label class="ctc__field-label">
                Description (EN)
                <span
                  v-if="translatingDescSource && translatingDescSource !== 'en'"
                  class="ctc__translating"
                  >Traduction…</span
                >
              </label>
              <input
                v-model="descEn"
                class="ctc__input"
                type="text"
                @blur="onDescBlur('en')"
              />
            </div>
            <div class="ctc__field">
              <label class="ctc__field-label">
                Description (NL)
                <span
                  v-if="translatingDescSource && translatingDescSource !== 'nl'"
                  class="ctc__translating"
                  >Traduction…</span
                >
              </label>
              <input
                v-model="descNl"
                class="ctc__input"
                type="text"
                @blur="onDescBlur('nl')"
              />
            </div>
          </div>
          <p v-if="createError" class="ctc__error">{{ createError }}</p>
          <div class="ctc__create-actions">
            <button
              type="button"
              class="ctc__btn ctc__btn--ghost"
              :disabled="creating"
              @click="closeCreateForm"
            >
              Annuler
            </button>
            <button
              type="button"
              class="ctc__btn ctc__btn--primary"
              :disabled="creating"
              @click="createCategory"
            >
              <i v-if="creating" class="pi pi-spin pi-spinner" />
              Créer la catégorie
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.ctc {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.ctc__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
}
.ctc__search {
  flex: 1;
  min-width: 12rem;
  height: 2.25rem;
  padding: 0 0.75rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.5rem;
  background: var(--nxt-card-bg);
  color: var(--nxt-on-surface);
  font-size: 0.8125rem;
  font-family: inherit;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;
}
.ctc__search:focus {
  outline: none;
  border-color: var(--nxt-primary);
  box-shadow: 0 0 0 3px rgba(var(--nxt-primary-rgb, 246, 132, 2), 0.15);
}
.ctc__count {
  font-size: 0.75rem;
  color: var(--nxt-on-surface-muted);
  white-space: nowrap;
}
.ctc__loading,
.ctc__empty {
  padding: 1.5rem 0.5rem;
  text-align: center;
  color: var(--nxt-on-surface-muted);
  font-size: 0.8125rem;
}
.ctc__list {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 16rem;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.625rem;
  padding: 0.375rem;
}
.ctc__item {
  border-radius: 0.5rem;
}
.ctc__label {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.5rem 0.625rem;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface);
  cursor: pointer;
  border-radius: 0.5rem;
}
.ctc__label:hover {
  background: var(--nxt-surface-mid);
}
.ctc__checkbox {
  position: relative;
  display: inline-flex;
  flex-shrink: 0;
}
.ctc__checkbox input {
  position: absolute;
  inset: 0;
  margin: 0;
  opacity: 0;
  cursor: pointer;
}
.ctc__checkbox-box {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.125rem;
  height: 1.125rem;
  border-radius: 0.3rem;
  border: 1.5px solid var(--nxt-outline-variant);
  background: var(--nxt-card-bg);
  color: transparent;
  font-size: 0.6875rem;
  transition:
    background 0.15s ease,
    border-color 0.15s ease,
    color 0.15s ease;
}
.ctc__checkbox input:checked ~ .ctc__checkbox-box {
  background: var(--nxt-primary);
  border-color: var(--nxt-primary);
  color: #fff;
}
[data-theme='dark'] .ctc__checkbox input:checked ~ .ctc__checkbox-box {
  background: #ffd60a;
  border-color: #ffd60a;
  color: #0b1320;
}
.ctc__checkbox input:focus-visible ~ .ctc__checkbox-box {
  outline: 2px solid var(--nxt-primary);
  outline-offset: 1px;
}
.ctc__toggle-create {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  align-self: flex-start;
  background: none;
  border: none;
  padding: 0;
  font-size: 0.8125rem;
  font-weight: 700;
  color: var(--nxt-on-surface-variant);
  cursor: pointer;
  font-family: inherit;
}
.ctc__create-collapse {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows 0.25s ease;
}
.ctc__create-collapse--open {
  grid-template-rows: 1fr;
}
.ctc__create-collapse-inner {
  overflow: hidden;
}
.ctc__create-form {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.75rem;
  background: var(--nxt-surface-low);
  margin-top: 0.75rem;
}
.ctc__create-hd {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}
.ctc__create-title {
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
}
.ctc__close-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 0.5rem;
  border: none;
  background: var(--nxt-surface-mid);
  color: var(--nxt-on-surface-variant);
  cursor: pointer;
  font-size: 0.75rem;
}
.ctc__close-btn:hover {
  background: var(--nxt-outline-variant);
}
.ctc__field-row {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}
.ctc__field {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
  min-width: 9rem;
}
.ctc__field-label {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.6875rem;
  font-weight: 700;
  color: var(--nxt-on-surface-muted);
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.ctc__translating {
  font-weight: 600;
  text-transform: none;
  letter-spacing: normal;
  color: var(--nxt-primary);
}
.ctc__input {
  height: 2.25rem;
  padding: 0 0.625rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.5rem;
  background: var(--nxt-card-bg);
  color: var(--nxt-on-surface);
  font-size: 0.8125rem;
  font-family: inherit;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;
}
.ctc__input:focus {
  outline: none;
  border-color: var(--nxt-primary);
  box-shadow: 0 0 0 3px rgba(var(--nxt-primary-rgb, 246, 132, 2), 0.15);
}
.ctc__error {
  margin: 0;
  font-size: 0.75rem;
  color: var(--nxt-error);
}
.ctc__create-actions {
  display: flex;
  justify-content: flex-end;
}
.ctc__btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  height: 2.25rem;
  padding: 0 1rem;
  border-radius: 0.5rem;
  font-size: 0.8125rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  border: none;
}
.ctc__btn--primary {
  background: #ffd60a;
  color: #0b1320;
}
.ctc__btn--primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.ctc__btn--ghost {
  background: var(--nxt-surface-mid);
  color: var(--nxt-on-surface-variant);
}
.ctc__btn--ghost:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
